import type { StatusTone } from '@/components';
import type {
  EventsProjectResponse,
  EventsProjectStatus,
} from '@/store/events/events-projects/events-projects.dto.ts';
import { formatCurrency } from '@/utils/formatCurrency';
import { formatDate } from '@/utils/formatDate';
import { timeAgo } from '@/utils/lastMessageTime';

export const PROJECT_STAGES: readonly EventsProjectStatus[] = [
  'exploring',
  'approved',
  'in_progress',
  'done',
  'cancelled',
];

export const STAGE_LABEL: Record<EventsProjectStatus, string> = {
  exploring: 'Exploring',
  approved: 'Approved',
  in_progress: 'In progress',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const STAGE_HINT: Record<EventsProjectStatus, string> = {
  exploring: 'Still an idea, or quotes waiting on a decision.',
  approved: 'A way chosen and the money agreed. Not started yet.',
  in_progress: 'Work is underway.',
  done: 'Finished.',
  cancelled: 'Decided against, or overtaken. It stays here so the thinking isn’t lost.',
};

const STAGE_TONE: Record<EventsProjectStatus, StatusTone> = {
  exploring: 'info',
  approved: 'success',
  in_progress: 'warning',
  done: 'neutral',
  cancelled: 'danger',
};

export const stageChip = (stage: EventsProjectStatus) => ({
  label: STAGE_LABEL[stage],
  tone: STAGE_TONE[stage],
});

export const INACTIVE_STAGES: readonly EventsProjectStatus[] = ['done', 'cancelled'];

export type StageFilter = 'all' | 'active' | EventsProjectStatus;

export const isActiveStage = (stage: EventsProjectStatus) => !INACTIVE_STAGES.includes(stage);

export const matchesStageFilter = (project: EventsProjectResponse, filter: StageFilter) => {
  if (filter === 'all') return true;
  if (filter === 'active') return isActiveStage(project.status);
  return project.status === filter;
};

export const SORT_LAST = Number.MAX_SAFE_INTEGER;

const DAY_MS = 24 * 60 * 60 * 1000;
const SOON_DAYS = 7;

export type DecideState = {
  label: string;
  overdue: boolean;
};

export const decideState = (project: EventsProjectResponse): DecideState | null => {
  if (!project.decide_by) return null;

  const due = new Date(project.decide_by).getTime();
  if (Number.isNaN(due)) return null;

  const days = Math.ceil((due - Date.now()) / DAY_MS);

  if (days < 0) return { label: `${Math.abs(days)}d overdue`, overdue: true };
  if (days === 0) return { label: 'Today', overdue: false };
  if (days <= SOON_DAYS)
    return { label: `In ${days} ${days === 1 ? 'day' : 'days'}`, overdue: false };

  return null;
};

export const decideByLabel = (project: EventsProjectResponse) =>
  project.decide_by ? formatDate(project.decide_by) : '';

export const decideBySortValue = (project: EventsProjectResponse) =>
  project.decide_by ? Date.parse(project.decide_by) : SORT_LAST;

export const budgetLabel = (project: EventsProjectResponse) => {
  const amount = Number(project.budget?.number);
  if (!project.budget || !Number.isFinite(amount)) return '';

  return `${formatCurrency(amount, { maximumFractionDigits: 0, minimumFractionDigits: 0 })} ${project.budget.currency}`;
};

export const budgetSortValue = (project: EventsProjectResponse) => {
  const amount = Number(project.budget?.number);
  return Number.isFinite(amount) ? amount : SORT_LAST;
};

export const chosenWay = (project: EventsProjectResponse) =>
  project.ways.find((way) => way.status === 'chosen');

export const leadingWay = (project: EventsProjectResponse) =>
  project.ways.find((way) => way.status === 'leading');

export const waysNote = (project: EventsProjectResponse) => {
  const chosen = chosenWay(project);
  if (chosen) return { text: `${chosen.name} chosen`, muted: false };

  const leading = leadingWay(project);
  if (leading) return { text: `${leading.name} leading`, muted: false };

  return { text: 'No leaning yet', muted: true };
};

export const lastActivityLabel = (project: EventsProjectResponse) => {
  const relative = timeAgo(project.updated_at || project.created_at);
  return relative ? `Updated ${relative}` : '';
};

export const byRecentActivity = (a: EventsProjectResponse, b: EventsProjectResponse) =>
  Date.parse(b.updated_at || b.created_at) - Date.parse(a.updated_at || a.created_at);
